export type DemandArea = "active" | "awaiting_acceptance" | "closed" | "trash";
export type DemandGrant = {
  access: boolean;
  create: boolean;
  edit: boolean;
  complete: boolean;
  share: boolean;
  transfer: boolean;
  send: boolean;
  readAll: boolean;
  profile: "advisor" | "chief" | "councillor";
};
export type DemandType = {
  id: string;
  name: string;
  active: boolean;
  subtypes: { id: string; name: string; active: boolean }[];
  subtypeRequired: boolean;
  documents: { id: string; name: string }[] | null;
  statuses: string[];
  points: string | null;
  xp: string | null;
};
export type DemandConfig = {
  grants: Record<string, DemandGrant>;
  types: DemandType[];
  rewardPolicy: "pending" | "exact-rational";
  ownerPolicy: "responsible";
  maxUploadMb: number;
  maxFiles: number;
};
export type CitizenData = { name: string; phone: string; address: string };
export type Citizen = CitizenData & {
  id: string;
  createdAt: string;
  version: number;
};
export type DemandAttachment = {
  fileId: string;
  documentType: string | null;
  validated: boolean;
};
export type DemandRecord = {
  id: string;
  citizenId: string;
  citizen: CitizenData;
  typeId: string;
  subtypeId: string | null;
  note: string;
  creatorId: string;
  responsibleId: string;
  collaboratorIds: string[];
  area: DemandArea;
  status: string;
  previousStatus?: string;
  priority: "Baixa" | "Média" | "Alta";
  deadline: string | null;
  deadlineTime: string | null;
  attachments: DemandAttachment[];
  createdAt: string;
  updatedAt: string;
  firstClosedAt?: string;
  firstClosedParticipants?: string[];
  closedAt?: string;
  conclusionNote?: string;
  deletedAt?: string;
  purgeAt?: string;
  previousArea?: Exclude<DemandArea, "trash">;
  version: number;
  rewardState?: "recorded" | "pending_configuration";
  syncState: "pending_configuration" | "pending" | "confirmed" | "failed";
  history: {
    id: string;
    actorId: string;
    at: string;
    action: string;
    text: string;
  }[];
};
export type DemandInput = {
  citizen: CitizenData;
  citizenId?: string;
  confirmNewCitizen?: boolean;
  confirmDuplicate?: boolean;
  typeId: string;
  subtypeId: string | null;
  note: string;
  priority: DemandRecord["priority"];
  deadline: string | null;
  deadlineTime: string | null;
  attachmentIds: string[];
  responsibleId?: string;
};
export type DemandDocumentState = {
  state: "unknown" | "none" | "pending" | "complete";
  label: string;
  missing: string[];
};
export const demandNormalize = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR")
    .trim()
    .replace(/\s+/g, " ");
export function demandDocumentState(
  record: Pick<DemandRecord, "attachments">,
  type?: DemandType,
): DemandDocumentState {
  if (!type || type.documents === null)
    return {
      state: "unknown",
      label: "Documentação não verificada",
      missing: [],
    };
  if (!type.documents.length)
    return {
      state: "none",
      label: "Nenhum documento obrigatório",
      missing: [],
    };
  const missing = type.documents
    .filter(
      (d) =>
        !record.attachments.some((a) => a.documentType === d.id && a.validated),
    )
    .map((d) => d.name);
  return {
    state: missing.length ? "pending" : "complete",
    label: missing.length ? "Documentos pendentes" : "Documentação completa",
    missing,
  };
}
